import React, { useCallback, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Helmet from 'react-helmet';

import {
  Participation,
  ParticipationStatus,
  Recruitment,
  Session,
  StudyType,
  useForm,
  useLogout,
  useMe,
  useParticipationExists,
} from '@app/api';

import { isSessionOpen, PublicBackground } from '@app/core';

import { CompletedSession } from './CompletedSession';
import {
  AnalyticsCategoryProvider,
  Categories,
  useTrackingParams,
} from '@app/analytics';
import { StudyDescription } from './StudyDescription/StudyDescription';
import { useLoginParticipantWithModal } from '@app/auth';
import { useDisplayedFieldsForCriteria } from './StudyProfile';
import { useTranslation } from 'react-i18next';

interface Props {
  session: Session;
  recruitment: Recruitment;
  participantId?: string;
  participation?: Participation | undefined;
  sessionLinkId?: string;
}

export const StudyLanding = ({
  session,
  recruitment,
  participantId,
  participation,
  sessionLinkId,
}: Props) => {
  const navigate = useNavigate();
  const tracking = useTrackingParams();
  const { data: form } = useForm(recruitment.screeningFormId);
  const login = useLoginParticipantWithModal();
  const { t } = useTranslation();

  const displayedFields = useDisplayedFieldsForCriteria(
    recruitment.criteria?.map((c) => c.fieldRef) || [],
  );

  const { data: me } = useMe(true);
  const { mutate: logout } = useLogout({ noRedirect: true });

  const { data: trackingParticipationExist } = useParticipationExists(
    tracking.tid,
  );

  useEffect(() => {
    if (me && participantId && !me.linkedParticipants.includes(participantId)) {
      logout();
    }
  }, [me, participantId, logout]);

  useEffect(() => {
    if (trackingParticipationExist) {
      navigate(`/p/${tracking.tid}`);
    }
  }, [trackingParticipationExist, tracking.tid]);

  const onStartApplying = useCallback(async () => {
    if (participantId && !me) {
      await login(participantId);
    }

    switch (true) {
      case form && form.fieldIds.length > 0:
        navigate('./screening');
        break;
      case session.studyType === StudyType.MODERATED &&
        !!session.allowedDevicesCategories?.length:
        navigate('./device');
        break;
      case !recruitment.allowTechParticipants:
        navigate('./job');
        break;
      case !!displayedFields?.length:
        navigate('./profile');
        break;
      default:
        navigate('./personal');
    }
  }, [
    participantId,
    me,
    form,
    session,
    displayedFields?.length,
    login,
    navigate,
    recruitment,
  ]);

  if (participation && participation.status !== ParticipationStatus.INVITED) {
    return <Navigate to={`/p/${participation.id}`} />;
  }

  if (!isSessionOpen(session)) {
    return (
      <PublicBackground title={t('application.completed.headerTitle')}>
        <CompletedSession
          sessionId={session.id}
          sessionLinkId={sessionLinkId}
        />
      </PublicBackground>
    );
  }

  return (
    <AnalyticsCategoryProvider category={Categories.SURVEY}>
      <Helmet>
        <title>{session.title} - Tandemz</title>
        <meta property="og:title" content={session.title} />
        <meta property="og:description" content={session.description} />
        <meta name="description" content={session.description} />
      </Helmet>
      <PublicBackground
        customContent
        title={t('participant.publicPageTitle.applyToStudies')}
      >
        <StudyDescription
          session={session}
          onClick={onStartApplying}
          sessionLinkId={sessionLinkId}
          participationId={participation?.id}
          participantId={participantId}
        />
      </PublicBackground>
    </AnalyticsCategoryProvider>
  );
};
